// The IDE's single source of truth. Every UI region binds to this store and is
// otherwise parameterless, so modules compose without prop-threading, the store
// IS the integration contract.
//
// Persistence: the virtual file system + a few UI prefs persist to localStorage
// so a reload keeps your work. Compile results are ephemeral (recomputed).

import { create } from "zustand";
import { getCompiler } from "./compiler/client";
import type { CompileResult, Diagnostic, Target } from "./types";
import { SEED_FILES } from "./fs/examples";
import { rewriteDepths } from "./panels/taptreeModel";
import type { Currency } from "./pricing";
import { DEFAULT_THEME, coerceTheme } from "./theme";
import type { ThemeId } from "./theme";
import { coerceNetwork } from "./network";
import type { Network } from "./network";

export type Theme = ThemeId;
/** Editor key handling: the default, or modal vim keys. */
export type EditorMode = "normal" | "vim";
export type CompileStatus = "idle" | "linting" | "compiling" | "ok" | "error";
export type ActivityView = "explorer" | "search" | "settings";
export type BottomTab =
  | "problems"
  | "output"
  | "script"
  | "certification"
  | "cost"
  | "lockfile";

export interface VFile {
  /** Unique path, e.g. "multisig.sl" or "multisig.args.json". */
  path: string;
  content: string;
  /** Editor language id. */
  language: "seal" | "json" | "plaintext";
}

// v7: the multisig and vault seeds use the non-malleable == M threshold (was
// >= M), matching the v0.2.0 compiler, which warns on >= M. Bumping the key
// discards the old seeds so a returning user gets the corrected set.
const LS_FILES = "seal-ide.files.v7";
// v3: the theme set changed (Tokyo Night is the default now); bumping discards a
// stale cached theme so the new default takes effect (still switchable any time).
const LS_PREFS = "seal-ide.prefs.v3";
// Explicit folder paths (so empty folders persist; non-empty ones are also
// implied by their files' paths, but we keep the set authoritative).
const LS_FOLDERS = "seal-ide.folders.v1";

interface Prefs {
  theme: Theme;
  fontSize: number;
  minimap: boolean;
  autoLint: boolean;
  wordWrap: boolean;
  editorMode: EditorMode;
  network: Network;
  allowUnproven: boolean;
  currency: Currency; // fiat for the taptree per-leaf spend cost
}

const DEFAULT_PREFS: Prefs = {
  theme: DEFAULT_THEME,
  fontSize: 13,
  minimap: true,
  autoLint: true,
  wordWrap: false,
  editorMode: "normal",
  network: "signet",
  allowUnproven: false,
  currency: "USD",
};

function loadFiles(): Record<string, VFile> {
  try {
    const raw = localStorage.getItem(LS_FILES);
    if (raw) return JSON.parse(raw);
  } catch {
    /* fall through to seed */
  }
  return { ...SEED_FILES };
}

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(LS_PREFS);
    if (raw) {
      const merged = { ...DEFAULT_PREFS, ...JSON.parse(raw) };
      // Heal a legacy or unknown theme ("dark"/"light" from before the named
      // themes) to a valid id so the data-theme attribute always resolves.
      merged.theme = coerceTheme(merged.theme);
      // Same for the network: mainnet is gone, and a stored "bc" (or an old
      // bech32m prefix, or junk) must never leave someone pointed at it.
      merged.network = coerceNetwork(merged.network);
      // An unknown stored value must not leave the editor in a mode that no
      // longer exists, with no keys working.
      if (merged.editorMode !== "vim") merged.editorMode = "normal";
      return merged;
    }
  } catch {
    /* defaults */
  }
  return { ...DEFAULT_PREFS };
}

function langFor(path: string): VFile["language"] {
  if (path.endsWith(".sl")) return "seal";
  if (path.endsWith(".json")) return "json";
  return "plaintext";
}

function loadFolders(): string[] {
  try {
    const raw = localStorage.getItem(LS_FOLDERS);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.filter((x) => typeof x === "string");
    }
  } catch {
    /* none */
  }
  return [];
}

function persistFolders(folders: string[]) {
  try {
    localStorage.setItem(LS_FOLDERS, JSON.stringify(folders));
  } catch {
    /* quota, non-fatal */
  }
}

/** Every ancestor folder path of `path` ("a/b/c" -> ["a", "a/b"]). */
function ancestorFolders(path: string): string[] {
  const parts = path.split("/").slice(0, -1);
  const out: string[] = [];
  let acc = "";
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p;
    out.push(acc);
  }
  return out;
}

/** The `.args.json` companion for a `.sl` file, by naming convention. */
export function argsPathFor(bsPath: string): string {
  return bsPath.replace(/\.sl$/, ".args.json");
}

export interface IdeState {
  files: Record<string, VFile>;
  /** Explicit folder paths (no trailing slash), e.g. "lib", "lib/crypto". */
  folders: string[];
  openTabs: string[];
  activePath: string | null;

  // Compilation
  status: CompileStatus;
  result: CompileResult | null; // last FULL compile (fund)
  /** Diagnostics for the active source file (from the latest lint or compile). */
  diagnostics: Diagnostic[];
  compileError: string | null;
  lastCompileMs: number | null;

  // UI
  prefs: Prefs;
  activityView: ActivityView;
  sidebarVisible: boolean;
  bottomVisible: boolean;
  bottomTab: BottomTab;
  commandPaletteOpen: boolean;

  // ---- actions ----
  openFile: (path: string) => void;
  closeTab: (path: string) => void;
  setActive: (path: string) => void;
  setContent: (path: string, content: string) => void;
  newFile: (path: string, content?: string) => void;
  renameFile: (from: string, to: string) => void;
  deleteFile: (path: string) => void;
  newFolder: (path: string) => void;
  renameFolder: (from: string, to: string) => void;
  deleteFolder: (path: string) => void;

  runCompile: (target?: Target) => Promise<void>;
  runLint: () => Promise<void>;
  /** Rewrite the active contract's `@depth` pins (from a taptree drag) and
   *  recompile. `null` for a name removes its pin. Auto-reverts if the rewrite
   *  somehow fails to compile, so a drag can never leave the source broken. */
  relayoutActive: (depthByName: Record<string, number | null>) => Promise<void>;

  setTheme: (t: Theme) => void;
  setPrefs: (p: Partial<Prefs>) => void;
  setActivityView: (v: ActivityView) => void;
  toggleSidebar: () => void;
  toggleBottom: () => void;
  setBottomTab: (t: BottomTab) => void;
  setCommandPalette: (open: boolean) => void;
}

function persistFiles(files: Record<string, VFile>) {
  try {
    localStorage.setItem(LS_FILES, JSON.stringify(files));
  } catch {
    /* quota, non-fatal */
  }
}

/** The active source file's path, if it's a `.sl` contract. */
function activeContract(s: Pick<IdeState, "activePath" | "files">): VFile | null {
  if (!s.activePath) return null;
  const f = s.files[s.activePath];
  return f && f.language === "seal" ? f : null;
}

/** The active Seal contract, resolving from an open `.args.json` sibling so the
 *  Output/Certification tabs can read the contract whether the `.sl` or its args
 *  file is focused. */
export function selectActiveContract(s: Pick<IdeState, "activePath" | "files">): VFile | null {
  const p = s.activePath;
  if (!p) return null;
  const slPath = p.endsWith(".args.json") ? p.replace(/\.args\.json$/, ".sl") : p;
  const f = s.files[slPath];
  return f && f.language === "seal" ? f : null;
}

/** The contract a path belongs to: a `.sl` file is its own, an `.args.json`
 *  belongs to its `.sl` sibling. `null` for anything that is not part of one. */
function contractPathOf(files: Record<string, VFile>, path: string | null): string | null {
  if (!path) return null;
  const slPath = path.endsWith(".args.json") ? path.replace(/\.args\.json$/, ".sl") : path;
  const f = files[slPath];
  return f && f.language === "seal" ? f.path : null;
}

/** Compile output belongs to the contract it came from. Moving to a different
 *  contract must drop it, or the panels keep showing the previous contract's
 *  address, taptree and lockfile under the new file's name. Switching between a
 *  contract and its own args file is not a move, so results survive that. */
function clearIfContractChanged(
  s: Pick<IdeState, "files" | "activePath">,
  nextActive: string | null,
  nextFiles?: Record<string, VFile>,
): Partial<IdeState> {
  const files = nextFiles ?? s.files;
  if (contractPathOf(s.files, s.activePath) === contractPathOf(files, nextActive)) return {};
  return {
    result: null,
    compileError: null,
    diagnostics: [],
    lastCompileMs: null,
    // Otherwise the status bar keeps reporting "Compiled" for output that is
    // no longer on screen.
    status: "idle",
  };
}

const initialFiles = loadFiles();
const initialPrefs = loadPrefs();

if (typeof document !== "undefined") {
  document.documentElement.dataset.theme = initialPrefs.theme;
}

let lintTimer: ReturnType<typeof setTimeout> | undefined;
let lintSeq = 0;
let compileSeq = 0;

export const useIde = create<IdeState>((set, get) => ({
  files: initialFiles,
  folders: loadFolders(),
  // Always open to the welcome screen, no contract loaded.
  openTabs: [],
  activePath: null,

  status: "idle",
  result: null,
  diagnostics: [],
  compileError: null,
  lastCompileMs: null,

  prefs: initialPrefs,
  activityView: "explorer",
  sidebarVisible: true,
  bottomVisible: false, // editor-dominant by default; opens on Compile
  bottomTab: "output",
  commandPaletteOpen: false,

  openFile: (path) =>
    set((s) => ({
      activePath: path,
      openTabs: s.openTabs.includes(path) ? s.openTabs : [...s.openTabs, path],
      ...clearIfContractChanged(s, path),
    })),

  closeTab: (path) =>
    set((s) => {
      const openTabs = s.openTabs.filter((p) => p !== path);
      const activePath =
        s.activePath === path ? (openTabs[openTabs.length - 1] ?? null) : s.activePath;
      return { openTabs, activePath, ...clearIfContractChanged(s, activePath) };
    }),

  setActive: (path) => set((s) => ({ activePath: path, ...clearIfContractChanged(s, path) })),

  setContent: (path, content) => {
    set((s) => {
      const f = s.files[path];
      if (!f) return {};
      const files = { ...s.files, [path]: { ...f, content } };
      persistFiles(files);
      return { files };
    });
    // Auto-lint the active contract on edit.
    if (get().prefs.autoLint) get().runLint();
  },

  newFile: (path, content = "") =>
    set((s) => {
      const files = {
        ...s.files,
        [path]: { path, content, language: langFor(path) },
      };
      persistFiles(files);
      // Materialize any ancestor folders the path implies so they persist even
      // if the file is later deleted.
      const need = ancestorFolders(path).filter((f) => !s.folders.includes(f));
      let folders = s.folders;
      if (need.length) {
        folders = [...s.folders, ...need];
        persistFolders(folders);
      }
      return {
        files,
        folders,
        openTabs: [...s.openTabs, path],
        activePath: path,
      };
    }),

  renameFile: (from, to) =>
    set((s) => {
      if (!s.files[from] || s.files[to]) return {};
      const { [from]: old, ...rest } = s.files;
      const files = { ...rest, [to]: { ...old, path: to, language: langFor(to) } };
      persistFiles(files);
      return {
        files,
        openTabs: s.openTabs.map((p) => (p === from ? to : p)),
        activePath: s.activePath === from ? to : s.activePath,
      };
    }),

  deleteFile: (path) =>
    set((s) => {
      if (!s.files[path]) return {};
      const { [path]: _removed, ...files } = s.files;
      persistFiles(files);
      const openTabs = s.openTabs.filter((p) => p !== path);
      const activePath =
        s.activePath === path ? (openTabs[openTabs.length - 1] ?? null) : s.activePath;
      return {
        files,
        openTabs,
        activePath,
        ...clearIfContractChanged(s, activePath, files),
      };
    }),

  newFolder: (path) =>
    set((s) => {
      if (s.folders.includes(path)) return {};
      const add = [...ancestorFolders(path), path].filter(
        (f) => !s.folders.includes(f),
      );
      const folders = [...s.folders, ...add];
      persistFolders(folders);
      return { folders };
    }),

  renameFolder: (from, to) =>
    set((s) => {
      if (from === to) return {};
      // Block a collision with an existing folder or a file under the new path.
      if (s.folders.includes(to) || s.files[to]) return {};
      const underFrom = (p: string) => p === from || p.startsWith(`${from}/`);
      const repath = (p: string) => to + p.slice(from.length);

      const files: Record<string, VFile> = {};
      for (const [p, f] of Object.entries(s.files)) {
        if (underFrom(p)) {
          const np = repath(p);
          files[np] = { ...f, path: np, language: langFor(np) };
        } else {
          files[p] = f;
        }
      }
      const folders = s.folders.map((f) => (underFrom(f) ? repath(f) : f));
      persistFiles(files);
      persistFolders(folders);
      const remap = (p: string | null) => (p && underFrom(p) ? repath(p) : p);
      return {
        files,
        folders,
        openTabs: s.openTabs.map((p) => (underFrom(p) ? repath(p) : p)),
        activePath: remap(s.activePath),
      };
    }),

  deleteFolder: (path) =>
    set((s) => {
      const under = (p: string) => p === path || p.startsWith(`${path}/`);
      const files: Record<string, VFile> = {};
      for (const [p, f] of Object.entries(s.files)) {
        if (!under(p)) files[p] = f;
      }
      const folders = s.folders.filter((f) => !under(f));
      persistFiles(files);
      persistFolders(folders);
      const openTabs = s.openTabs.filter((p) => !under(p));
      return {
        files,
        folders,
        openTabs,
        activePath:
          s.activePath && under(s.activePath)
            ? (openTabs[openTabs.length - 1] ?? null)
            : s.activePath,
      };
    }),

  runCompile: async (target: Target = "fund") => {
    const s = get();
    const c = selectActiveContract(s);
    // Say so rather than doing nothing: pressing Compile with no contract open
    // (or with an args file focused and no sibling) is otherwise a dead button.
    if (!c) {
      set({
        status: "error",
        compileError: "Open a .sl contract to compile.",
        bottomVisible: true,
        bottomTab: "output",
      });
      return;
    }
    const argsFile = s.files[argsPathFor(c.path)];
    const args = argsFile ? argsFile.content : null;
    // Open the results panel but stay on whatever tab is active (so compiling
    // from the Taptree tab keeps you there, watching the tree update in place).
    // First load initializes bottomTab to "output", so the very first compile
    // still lands there.
    const seq = ++compileSeq;
    set({ status: "compiling", compileError: null, bottomVisible: true });
    try {
      const result = await getCompiler().compile(c.content, {
        args,
        target,
        allowUnproven: s.prefs.allowUnproven,
        network: s.prefs.network,
      });
      // The `fund` target doesn't compute spend costs (only the `cost` target
      // does). For the IDE, a single Compile should populate the Cost tab too,
      // the cost pass assembles without the gate, so it works even when funding
      // is refused. One extra ~30ms wasm call; merge its costs into the result.
      if (target === "fund" && result.leaves) {
        try {
          const costRun = await getCompiler().compile(c.content, {
            args,
            target: "cost",
            allowUnproven: s.prefs.allowUnproven,
            network: s.prefs.network,
          });
          if (costRun.costs) result.costs = costRun.costs;
        } catch {
          /* cost is supplementary; ignore a failure */
        }
      }
      if (seq !== compileSeq) return; // superseded by a newer compile
      set({
        result,
        status: result.ok ? "ok" : "error",
        diagnostics: result.diagnostics ?? [],
        lastCompileMs: null,
      });
    } catch (e) {
      if (seq !== compileSeq) return;
      set({ status: "error", compileError: e instanceof Error ? e.message : String(e) });
    }
  },

  relayoutActive: async (depthByName) => {
    const s = get();
    const c = activeContract(s);
    if (!c) return;
    const prev = c.content;
    const next = rewriteDepths(prev, depthByName);
    if (next === prev) return; // already in the requested shape
    get().setContent(c.path, next);
    await get().runCompile("fund");
    // A valid binary-tree drag is always Kraft-valid, so this should never
    // fire; if it somehow does (e.g. a pathological source the regex mangled),
    // restore the previous source rather than leave the contract broken.
    if (get().status === "error") {
      get().setContent(c.path, prev);
      await get().runCompile("fund");
    }
  },

  runLint: async () => {
    if (lintTimer) clearTimeout(lintTimer);
    lintTimer = setTimeout(async () => {
      const s = get();
      const c = activeContract(s);
      if (!c) {
        set({ diagnostics: [] });
        return;
      }
      const argsFile = s.files[argsPathFor(c.path)];
      const seq = ++lintSeq;
      set({ status: "linting" });
      try {
        const result = await getCompiler().check(c.content, argsFile ? argsFile.content : null);
        if (seq !== lintSeq) return; // superseded by a newer keystroke
        set({ diagnostics: result.diagnostics ?? [], status: "idle" });
      } catch {
        if (seq === lintSeq) set({ status: "idle" });
      }
    }, 150);
  },

  setTheme: (theme) => {
    document.documentElement.dataset.theme = theme;
    get().setPrefs({ theme });
  },

  setPrefs: (p) =>
    set((s) => {
      const prefs = { ...s.prefs, ...p };
      try {
        localStorage.setItem(LS_PREFS, JSON.stringify(prefs));
      } catch {
        /* non-fatal */
      }
      return { prefs };
    }),

  setActivityView: (activityView) =>
    set((s) => ({
      activityView,
      sidebarVisible: s.activityView === activityView ? !s.sidebarVisible : true,
    })),
  toggleSidebar: () => set((s) => ({ sidebarVisible: !s.sidebarVisible })),
  toggleBottom: () => set((s) => ({ bottomVisible: !s.bottomVisible })),
  setBottomTab: (bottomTab) => set({ bottomTab, bottomVisible: true }),
  setCommandPalette: (commandPaletteOpen) => set({ commandPaletteOpen }),
}));
